import { useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { toast } from '../../../shared/useToast.js';

/**
 * طباعة وتصدير كشف حساب البطاقة.
 *
 * الطباعة: نفس نهج `print-root` + `print.css` المستعمل في شاشة الديون وكشف
 * حساب المورد — بلا مكتبة طباعة إضافية.
 * التصدير: استيراد كسول لمكتبة `xlsx` الموجودة أصلاً في المشروع (لا تبعية جديدة).
 *
 * ملاحظة الصلاحية: لا نستعمل `reports/components/ExportButton` *وحده* لأنه
 * محجوب بـ `reports.export`، وموظف الاستقبال الذي يُصدر البطاقات ويعبّئها لا
 * يملكها عادةً فكان زر الطباعة يختفي عنه. هذان الزران متاحان لكل من يرى
 * الكشف، وزرّ التصدير الشامل (CSV/PDF) يُعرَض إضافةً لمن يملك الصلاحية.
 *
 * الملف يحمل *نفس* ما يراه المستخدم: معلومات البطاقة، وحركات المدة المختارة
 * بعد كل الفلاتر النشطة، والإجماليات — فلا يمكن أن يختلف المصدَّر عن المعروض.
 */

const IQD_FORMAT = '#,##0" د.ع"';

function cellValue(row, col) {
  return typeof col.value === 'function' ? col.value(row) : row[col.key];
}

async function exportExcel({ rows, columns, filename, title, info, totals }) {
  const XLSX = await import('xlsx');

  const header = columns.map((c) => c.label);
  const body = rows.map((r) => columns.map((c) => cellValue(r, c)));

  // سطور الترويسة: العنوان ومعلومات البطاقة والفلاتر — حتى يُعرف الملف بعد حفظه.
  const head = [[title]];
  (info || []).forEach((line) => head.push([line]));
  head.push([]);

  const foot = [[]];
  (totals || []).forEach((line) => foot.push(line));

  const ws = XLSX.utils.aoa_to_sheet([...head, header, ...body, ...foot]);
  // ملاحظة: نسخة xlsx المجتمعية (0.18.5) لا تكتب اتجاه الورقة فعلياً، لكن
  // المحتوى عربي والأعمدة معنونة فيُقرأ سليماً. نتركها لتعمل عند الترقية.
  ws['!rtl'] = true;
  ws['!cols'] = columns.map((c) => ({ wch: c.width || 16 }));

  const headerRows = head.length;
  // أعمدة المبالغ تُكتب أرقاماً بتنسيق عملة لا نصاً، فتُجمع في Excel.
  columns.forEach((col, ci) => {
    if (!col.money) return;
    for (let ri = 0; ri < body.length; ri += 1) {
      const ref = XLSX.utils.encode_cell({ r: headerRows + 1 + ri, c: ci });
      const cell = ws[ref];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = IQD_FORMAT;
      }
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'كشف حساب البطاقة');
  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

export function StatementExportButtons({
  rows, columns, filename, title, info, totals, disabled
}) {
  const [busy, setBusy] = useState(false);
  const hasData = Array.isArray(rows) && rows.length > 0;

  async function onExcel() {
    if (!hasData) {
      toast.error('لا توجد حركات للتصدير');
      return;
    }
    setBusy(true);
    try {
      await exportExcel({ rows, columns, filename, title, info, totals });
      toast.success(`تم تصدير ${rows.length} حركة إلى Excel`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('card statement export error:', err);
      toast.error('تعذّر التصدير');
    } finally {
      setBusy(false);
    }
  }

  function onPrint() {
    // الكشف يُطبع حتى لو كان بلا حركات: الورقة الرسمية تحمل معلومات البطاقة
    // والإجماليات، و«لا حركات في هذه المدة» معلومة صحيحة تُطبع.
    window.print();
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-primary"
        data-testid="statement-print"
        onClick={onPrint}
        disabled={disabled || busy}
      >
        <Icon name="print" size={13} /> طباعة كشف الحساب
      </button>
      <button
        type="button"
        className="btn"
        data-testid="statement-excel"
        onClick={onExcel}
        disabled={disabled || busy}
      >
        <Icon name="reports" size={13} /> {busy ? 'جاري التصدير...' : 'تصدير Excel'}
      </button>
    </>
  );
}
